import { Button, Checkbox, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { isArray } from 'lodash';
import React, {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';

const {
  actions: categoryActions,
  selectors: categorySelectors
} = slices.category;

interface Props {
  onHandleCategories: (categoryIds: number | number[]) => void; // Update to handle multiple categories
  isMultiple?: boolean; // Allow support for multiple selections
  isDisabled?: boolean; // Disable the dropdown if needed
  initialValue?: number | number[];
  size?: 'medium' | 'small';
  setCategoryIsBlurred?: (isBlurred: boolean) => void;
}

const Component: FC<Props> = ({
  onHandleCategories,
  isMultiple = false,
  isDisabled = false,
  initialValue = null,
  size = 'medium', // default size
  setCategoryIsBlurred
}) => {
  const dispatch = useAppDispatch();
  const categories = useAppSelector(categorySelectors.selectCategories);

  const [selectedCategories, setSelectedCategories] = useState<
    any | any[] | null
  >(isMultiple ? [] : null);

  const getCategories = useCallback(async () => {
    try {
      await dispatch(categoryActions.getCategoriesThunk({}));
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  const onHandleSelectedChangeCategory = (
    event: ChangeEvent<{}>,
    value: any[] | any | null
  ) => {
    setSelectedCategories(value);
    if (isArray(value)) {
      const categoryIds = value ? value.map((item: any) => item.id) : [];
      onHandleCategories(categoryIds); // Handle multiple or single category IDs
    }
    onHandleCategories(value?.id);
  };
  const initializeSelectedCategories = useCallback(() => {
    if (!categories || categories.length === 0 || initialValue == null) return;

    if (isMultiple && Array.isArray(initialValue)) {
      const matched = categories.filter((cat) => initialValue.includes(cat.id));
      setSelectedCategories(matched);
    } else if (!isMultiple && typeof initialValue === 'number') {
      const matched = categories.find((cat) => cat.id === initialValue);
      if (matched) setSelectedCategories(matched);
    }
  }, [categories, initialValue, isMultiple]);

  useEffect(() => {
    initializeSelectedCategories();
  }, [initialValue, categories, isMultiple, initializeSelectedCategories]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  return (
    <div>
      <div>
        <Autocomplete
          style={{ background: 'white' }}
          limitTags={1}
          size={size}
          id="category-list-dropdown"
          options={categories}
          fullWidth
          multiple={isMultiple} // Support for multiple selections
          value={selectedCategories}
          getOptionLabel={(option) => option.name || ''}
          onChange={onHandleSelectedChangeCategory}
          renderOption={(option, { selected }) =>
            isMultiple ? (
              <>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                {option.name}
              </>
            ) : (
              <>{option.name}</>
            )
          }
          renderInput={(params) => (
            <TextField {...params} label="Categories" variant="outlined" />
          )}
          disabled={isDisabled}
          onBlur={() => {
            setCategoryIsBlurred?.(true);
          }}
          onFocus={() => {
            setCategoryIsBlurred?.(false);
          }}
        />
      </div>
      <Button
        variant="outlined"
        color="primary"
        size='small'
        disabled={isDisabled}
        onClick={() => {
          setSelectedCategories(categories);
          onHandleCategories(isMultiple ? categories.map((cat) => cat.id) : categories[0]?.id);
        }}
      >
        Select All
      </Button>
    </div>
  );
};

export const CategoryListDropdown = memo(Component);
